import { qualtricsToken } from '../../common/token';
import { qualtricsSurvey, supportInsightFindings } from '../../common/urls';
import { commerceSwat as commerceSwatUrl } from '../../common/urls';
import { getSelectedOrg } from './support-org-util';

const DUMMY_INSIGHTS = [
  {
    id: 2,
    title: 'Weak Password Policy',
    recommendation_compiled: `## Total Segmentation Definition Counts\n\n@{{ datapoint }}\n\nWhen the segment count approaches the 4,000 limit, it can cause platform-wide performance degradation and block new segments. To reduce the count, implement segment governance, regular reviews and cleanups.\n\n## Description\n\nThe current total number of segmentation definitions in your environment is nearing or exceeding the platform limit of 4,000. This limit exists to protect the stability and performance of Adobe Experience Platform services, as segmentation jobs grow in complexity and duration with increased segment counts.\n\nYou may experience the following issues when approaching or crossing the 4,000-segment threshold:\n\n* Significant delays in daily or batch segmentation jobs.\n* Slower loading of the Segments browse and management UI.\n* Errors or inability to create new segments due to system-enforced limits.\n* Platform-wide performance degradation affecting destinations, activation, and analytics.\n\nAddressing the segment count proactively is critical to ensuring uninterrupted use of Adobe Experience Platform segmentation and related features.\n\n## Steps to resolve\n\n* Identify data owners and set internal guidelines for how and when segments are created.\n* Schedule regular reviews of all segments to check if they are still needed. Archive or delete those that are not actively in use.\n* Check that segments you want to delete are not referenced by others; remove dependencies first if needed.\n* Use reporting or APIs to list all segments and check usage or modification dates.\n* Tag and categorize segments to improve discoverability and auditing.\n* Merge similar segments to reduce redundancy and overlap.\n* Ensure every segment has a clear name and description.\n* Use automation tools like the Platform API or open-source SDK to help with cleanup if the volume is high.\n\n## Root cause\n\nThis happens when old or unused segments accumulate and governance processes are missing or unclear. Too many segments can slow down segmentation jobs and UI performance and eventually block new segment creation. Avoid this by maintaining regular review routines and clear guidelines for segment creation and removal.\n\nIf the issue persists, submit a support ticket in the [Support section of the Adobe Experience League portal](https://experienceleague.adobe.com/home?support-tab=home#support).\n\n## Related reading\n\n[Segmentation guardrails](https://experienceleague.adobe.com/en/docs/experience-platform/profile/guardrails#segmentation-guardrails)\n`, 
    impact: 'MEDIUM',
    org_id: 1,
    product_id: 1,
    project_id: 1,
    environment_id: 1,
    host_name: 'prod-server-01',
    created_at: '2026-01-05T03:59:18.487Z',
    updated_at: '2026-01-05T03:59:18.487Z',
    source_declaration_id: 2,
    recommendation_template: 'Implement stronger password requirements with minimum 12 characters',
    product_code: 'AEM',
    product_family_code: 'Adobe Experience Manager',
    org_id_source: 'ORG001',
    org_name: 'Adobe Marketing Cloud',
    project_name: 'Project Alpha',
    environment_name: 'production'
  },
  {
    id: 1,
    title: 'Total Segmentation Definition Counts',
    recommendation_compiled: `## Total Segmentation Definition Counts\n\n@{{ datapoint }}\n\nWhen the segment count approaches the 4,000 limit, it can cause platform-wide performance degradation and block new segments. To reduce the count, implement segment governance, regular reviews and cleanups.\n\n## Description\n\nThe current total number of segmentation definitions in your environment is nearing or exceeding the platform limit of 4,000. This limit exists to protect the stability and performance of Adobe Experience Platform services, as segmentation jobs grow in complexity and duration with increased segment counts.\n\nYou may experience the following issues when approaching or crossing the 4,000-segment threshold:\n\n* Significant delays in daily or batch segmentation jobs.\n* Slower loading of the Segments browse and management UI.\n* Errors or inability to create new segments due to system-enforced limits.\n* Platform-wide performance degradation affecting destinations, activation, and analytics.\n\nAddressing the segment count proactively is critical to ensuring uninterrupted use of Adobe Experience Platform segmentation and related features.\n\n## Steps to resolve\n\n* Identify data owners and set internal guidelines for how and when segments are created.\n* Schedule regular reviews of all segments to check if they are still needed. Archive or delete those that are not actively in use.\n* Check that segments you want to delete are not referenced by others; remove dependencies first if needed.\n* Use reporting or APIs to list all segments and check usage or modification dates.\n* Tag and categorize segments to improve discoverability and auditing.\n* Merge similar segments to reduce redundancy and overlap.\n* Ensure every segment has a clear name and description.\n* Use automation tools like the Platform API or open-source SDK to help with cleanup if the volume is high.\n\n## Root cause\n\nThis happens when old or unused segments accumulate and governance processes are missing or unclear. Too many segments can slow down segmentation jobs and UI performance and eventually block new segment creation. Avoid this by maintaining regular review routines and clear guidelines for segment creation and removal.\n\nIf the issue persists, submit a support ticket in the [Support section of the Adobe Experience League portal](https://experienceleague.adobe.com/home?support-tab=home#support).\n\n## Related reading\n\n[Segmentation guardrails](https://experienceleague.adobe.com/en/docs/experience-platform/profile/guardrails#segmentation-guardrails)\n`,
    impact: 'HIGH',
    org_id: 1,
    product_id: 1,
    project_id: 1,
    environment_id: 1,
    host_name: 'prod-server-01',
    created_at: '2026-01-05T03:59:18.487Z',
    updated_at: '2026-01-05T03:59:18.487Z',
    source_declaration_id: 1,
    recommendation_template: 'When the segment count approaches the 4,000 limit, it can cause platform-wide performance degradation',
    product_code: 'AEP',
    product_family_code: 'Adobe Experience Platform',
    org_id_source: 'ORG001',
    org_name: 'Adobe Marketing Cloud',
    project_name: 'Project Alpha',
    environment_name: 'production'
  }
];
import { request } from '../../common/request';
//Fetch insights
export async function fetchInsightsByDescription({ orgId = '', productId = '', description = '' } = {}) {
  if (!description) {
    console.log('No description, skipping insights fetch');
    return null;
  }
  console.log('fetchInsightsByDescription', { orgId, productId, description });
  try {
    //http://localhost:3000/api/v1/orgs/ORG001/products/AEM/findings/search
    const url = supportInsightFindings.replace("ORG_ID", "ORG001").replace("PRODUCT_CODE", "AEM")
    const res = await request(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        query: 'How to fix authentication issues?',
        top_k: 5,
      })
    });
    
    if (!res.ok) {
      console.error('Insights API failed with status:', res.status);
      return null;
    }
    
    const data = await res.json();
    const findingsList = data.results.map(item => item.finding);

    //   console.log("data ======> ", findingsList)
    // console.log('Support Insights API Call:', { orgId, productId, description });
    return findingsList;
  } catch (error) {
    console.error('Error fetching insights:', error);
    return null;
  }
}

//Submit feedback for an insight
export async function submitInsightFeedback({ type, feedback, insightTitle, projectUrl, root }) {
  try {
    const url = qualtricsSurvey; // dynamic url
    const selectedButton = root?.querySelector('#product-btn-container .product-btn-selected');
    const selectedProductName = selectedButton?.innerText?.trim();
    const userData = JSON.parse(sessionStorage.getItem('profile')) || {};
    const userEmail = userData?.email || '';
    const current_org = getSelectedOrg();
    const orgName= current_org?.name || '';
    const portal = "legacy"
    const requestBody = {
      values: {
        QID3: type==='up' ? 1 : 2,
        QID4_TEXT: feedback,
        QID5_TEXT: userEmail,
        QID9_TEXT: orgName,
        QID10_TEXT: selectedProductName,
        QID11_TEXT: insightTitle,
        QID13_TEXT: projectUrl,
        QID16: 1
      }
    };
    const qualtricsTokenValue = await qualtricsToken();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-API-TOKEN': qualtricsTokenValue, // dynamic token
        // Optional but recommended
        // 'Idempotency-Key': 'your-unique-key'
      },
      body: JSON.stringify(requestBody)
    });

    const data = await res.json();
    if (res && res.ok && data?.meta?.httpStatus.includes('200')) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('error in submitting feedback', error);
    return false;
  }
}

export async function fetchCommerceSwatResult({ projectId = '', environment = 'production', email = '', orgID = '' } = {}) {
  try{
    let userDetails = await adobeIMS.getProfile();
    const res = await request(commerceSwatUrl, {
      credentials: 'include',
      headers: {
        Authorization : `Bearer ${window.adobeIMS.getAccessToken()?.token || ''}`,
        'content-type': 'application/json',
      },
      params:{
        auth_id: userDetails.userId,
        projectId,
        environment,
        email,
        orgID: getSelectedOrg()?.id || ''
      },
      method: 'GET'
    });
    if (res) {
      const serverData = await res.json();
      if(serverData.success){

        const recommendations = Array.isArray(serverData?.recommendations?.results) ? serverData.recommendations.results : [];
        console.log('recommendations swat result', recommendations);
        const fliteredRecommendationMap = new Map();
        recommendations.forEach(recommendation => {
          const recommendationCode = recommendation.code;
          if (!fliteredRecommendationMap.has(recommendationCode)) {
            fliteredRecommendationMap.set(recommendationCode, recommendation);
          }
          else{
            const existingRecommendation = fliteredRecommendationMap.get(recommendationCode);
            if (new Date(existingRecommendation.checkedAt) < new Date(recommendation.checkedAt)) {
              fliteredRecommendationMap.set(recommendationCode, recommendation);
            }
          }
        });
        const filteredRecommendations = Array.from(fliteredRecommendationMap.values());
        console.log('filteredRecommendations', filteredRecommendations);
        
        return filteredRecommendations;
      } else{
        return {error: 'unauthorized'}
      }
    }
  } catch (error) {
    console.error('error in fetching swat result swat result', error);
    console.log('error', error);
    
    return {error};
  }
}

//Download insight as PDF or other format
export async function downloadInsight(insight) {
  // TODO: Implement download functionality
  // This could export as PDF, Word doc, or other formats
}